import { __decorate } from "tslib";
import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import chevronLeftIcon from '../../assets/icons/chevron-left';
import chevronRightIcon from '../../assets/icons/chevron-right';
import '../icon';
import '../button';
import '../typography/tg-caption';
import '../typography/tg-label-two';
import styles from './styles';
/**
 * @dependency amityeko-icon
 * @dependency amityeko-button
 * @dependency amityeko-tg-caption
 * @dependency amityeko-tg-label-two
 *
 * @event amityeko-select {{ selected }} - Emitted when an item is selected.
 */
export default class AmityekoImageSelector extends LitElement {
    constructor() {
        super(...arguments);
        // TODO: add actual logic - only visual at the moment provided
        this.required = false;
        this.assets = [];
        this.visibleCount = 3;
        this.scrollStep = 1;
        this.itemWidth = '82px';
        this.itemHeight = '82px';
        this.hasAsterisk = false;
        this.leftIndex = 0;
    }
    get shouldShowButtons() {
        if (this.assets.length <= this.visibleCount)
            return false;
        return true;
    }
    /** Resets the data in the element. */
    reset() {
        this.selectedId = this.assets[0].id;
    }
    _onClickItem(e, id) {
        e.stopPropagation();
        this.selectedId = id;
        emit(this, 'amityeko-select', { detail: { selected: this.selectedId } });
    }
    _onClickLeftScroll(e) {
        e.stopPropagation();
        const approxLeftIndex = this.leftIndex - this.scrollStep;
        this.leftIndex = approxLeftIndex >= 0 ? approxLeftIndex : 0;
    }
    _onClickRightScroll(e) {
        e.stopPropagation();
        const maxLeftIndex = this.assets.length - this.visibleCount;
        const approxLeftIndex = this.leftIndex + this.scrollStep;
        this.leftIndex = approxLeftIndex <= maxLeftIndex ? approxLeftIndex : maxLeftIndex;
    }
    _renderItem(item) {
        if (item.type === 'image') {
            return html `<img
        src="${item.src}"
        part="item"
        @click=${(e) => this._onClickItem(e, item.id)}
        class="item"
      />`;
        }
        if (item.type === 'gradient') {
            return html `<div
        @click=${(e) => this._onClickItem(e, item.id)}
        class="item item--gradient"
        part="item"
        style=${styleMap({
                '--private-from-color': item.fromColor,
                '--private-to-color': item.toColor,
            })}
      ></div>`;
        }
        if (item.type === 'bg-image') {
            return html `<div
        part="item"
        @click=${(e) => this._onClickItem(e, item.id)}
        class="item item--bgimage"
        style=${styleMap({
                '--private-bg-image': item.src,
            })}
      ></div>`;
        }
        return null;
    }
    render() {
        if (this.assets.length === 0)
            return null;
        const visibleItems = this.assets.slice(this.leftIndex, this.leftIndex + this.visibleCount);
        const isLeftScrollDisabled = this.leftIndex <= 0;
        const isRightScrollDisabled = this.leftIndex >= this.assets.length - this.visibleCount;
        return html `
      <div part="base" class="selector">
        ${this.label
            ? html `
              <amityeko-tg-label-two part="label" class="selector__label">
                ${this.label} ${this.required && this.hasAsterisk ? '*' : null}
              </amityeko-tg-label-two>
            `
            : null}

        <div part="select" class="selector__select">
          ${this.shouldShowButtons
            ? html `
                <amityeko-button
                  circle
                  variant="floating"
                  ?disabled=${isLeftScrollDisabled}
                  @click=${this._onClickLeftScroll}
                  class="selector__button--left"
                >
                  <amityeko-icon .iconSize=${'12px'}>${chevronLeftIcon}</amityeko-icon>
                </amityeko-button>
              `
            : null}
          ${visibleItems.map(item => html `
              <div
                class="item-container ${classMap({
            'item-container--selected': this.selectedId === item.id,
        })}"
                style=${styleMap({
            '--private-item-width': this.itemWidth,
            '--private-item-height': this.itemHeight,
        })}
              >
                ${this._renderItem(item)}
                ${item.name
            ? html `<amityeko-tg-caption class="item__name">${item.name}</amityeko-tg-caption>`
            : null}
              </div>
            `)}
          ${this.shouldShowButtons
            ? html `
                <amityeko-button
                  circle
                  variant="floating"
                  ?disabled=${isRightScrollDisabled}
                  @click=${this._onClickRightScroll}
                  class="selector__button--right"
                >
                  <amityeko-icon .iconSize=${'12px'}>${chevronRightIcon}</amityeko-icon>
                </amityeko-button>
              `
            : null}
        </div>
      </div>
    `;
    }
}
AmityekoImageSelector.styles = styles;
__decorate([
    property({ type: Boolean })
], AmityekoImageSelector.prototype, "required", void 0);
__decorate([
    property()
], AmityekoImageSelector.prototype, "label", void 0);
__decorate([
    property({ type: Array })
], AmityekoImageSelector.prototype, "assets", void 0);
__decorate([
    property({ type: Number })
], AmityekoImageSelector.prototype, "visibleCount", void 0);
__decorate([
    property({ type: Number })
], AmityekoImageSelector.prototype, "scrollStep", void 0);
__decorate([
    property()
], AmityekoImageSelector.prototype, "selectedId", void 0);
__decorate([
    property()
], AmityekoImageSelector.prototype, "itemWidth", void 0);
__decorate([
    property()
], AmityekoImageSelector.prototype, "itemHeight", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoImageSelector.prototype, "hasAsterisk", void 0);
__decorate([
    state()
], AmityekoImageSelector.prototype, "leftIndex", void 0);
if (!customElements.get('amityeko-image-selector')) {
    customElements.define('amityeko-image-selector', AmityekoImageSelector);
}
//# sourceMappingURL=index.js.map